import React, { useState, useEffect } from 'react';
import { auditAPI } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Button } from '../components/ui/button';
import { Loader2, RefreshCw, FileSearch } from 'lucide-react';

const ACTIONS = ['create', 'update', 'delete', 'login', 'approve', 'reject', 'upload', 'download', 'verify', 'password_reset', 'role_change', 'lock', 'unlock'];
const MODULES = ['auth', 'users', 'employees', 'expenses', 'leaves', 'documents', 'onboarding', 'holidays'];

const ACTION_COLOR = {
  create: 'default',
  update: 'secondary',
  delete: 'destructive',
  login: 'outline',
  approve: 'default',
  reject: 'destructive',
  upload: 'secondary',
  download: 'outline',
  verify: 'default',
  password_reset: 'destructive',
  role_change: 'destructive',
  lock: 'destructive',
  unlock: 'default',
};

const AuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterAction, setFilterAction] = useState('all');
  const [filterModule, setFilterModule] = useState('all');
  const [searchUser, setSearchUser] = useState('');

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (filterAction !== 'all') params.action = filterAction;
      if (filterModule !== 'all') params.module = filterModule;
      const res = await auditAPI.list(params);
      setLogs(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLogs(); /* eslint-disable-next-line */ }, [filterAction, filterModule]);

  const filtered = logs.filter(l =>
    !searchUser || (l.user_email || '').toLowerCase().includes(searchUser.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="audit-logs-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><FileSearch /> Audit Logs</h1>
          <p className="text-muted-foreground">System activity & compliance tracking</p>
        </div>
        <Button variant="outline" onClick={fetchLogs}><RefreshCw size={16} className="mr-2" />Refresh</Button>
      </div>

      <Card>
        <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div><Label className="text-xs">Search by user email</Label><Input value={searchUser} onChange={(e) => setSearchUser(e.target.value)} placeholder="user@example.com" data-testid="audit-search-user" /></div>
          <div><Label className="text-xs">Action</Label>
            <Select value={filterAction} onValueChange={setFilterAction}>
              <SelectTrigger data-testid="audit-filter-action"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All actions</SelectItem>
                {ACTIONS.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Module</Label>
            <Select value={filterModule} onValueChange={setFilterModule}>
              <SelectTrigger data-testid="audit-filter-module"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All modules</SelectItem>
                {MODULES.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? <div className="p-8 flex justify-center"><Loader2 className="h-8 w-8 animate-spin" /></div> : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Time</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Module</TableHead>
                  <TableHead>Entity</TableHead>
                  <TableHead>Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No logs found</TableCell></TableRow>
                ) : filtered.map(l => (
                  <TableRow key={l.id} data-testid={`audit-row-${l.id}`}>
                    <TableCell className="text-xs">{new Date(l.timestamp).toLocaleString()}</TableCell>
                    <TableCell className="text-sm">{l.user_email || '—'}</TableCell>
                    <TableCell><Badge variant="outline" className="text-xs">{l.user_role}</Badge></TableCell>
                    <TableCell><Badge variant={ACTION_COLOR[l.action] || 'secondary'}>{l.action}</Badge></TableCell>
                    <TableCell className="text-sm">{l.module}</TableCell>
                    <TableCell className="text-xs font-mono">{l.entity_type}/{l.entity_id?.slice(0, 8)}</TableCell>
                    <TableCell className="text-xs max-w-xs truncate" title={JSON.stringify(l.details)}>
                      {l.details && Object.keys(l.details).length > 0 ? JSON.stringify(l.details) : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AuditLogs;
